/**
 * @prettier
 * @flow
 */
import { useMediaQuery } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { BigNumber } from 'bignumber.js';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { paymentLinkApi } from 'src/api/paymentLinkApi';
import { theme } from 'src/AppRoot';
import { Form } from 'src/components/form/Form';
import { FormCardDetailsField } from 'src/components/form/FormCardDetailsField';
import { FormContactDetailsField } from 'src/components/form/FormContactDetailsField';
import { FormShippingDetailsField } from 'src/components/form/FormShippingDetailsField';
import { PaymentLinkStatuses } from 'src/constants/PaymentLinkStatus';
import openlinealogo from 'src/images/openlinealogo.png';
import { InvalidPaymentLink } from 'src/scenes/payment-link-page/InvalidPaymentLink';
import { Loading } from 'src/scenes/payment-link-page/Loading';
import { PaymentLinkIsPaid } from 'src/scenes/payment-link-page/PaymentLinkIsPaid';
import { PaymentNotAvailable } from 'src/scenes/payment-link-page/PaymentNotAvailable';
import type { EmailAddress, PhoneNumber } from 'src/types/Id';
import { alertKnownErrorOrSomethingWentWrong } from 'src/utils/alert/alertKnownErrorOrSomethingWentWrong';
import { formatAsCurrency } from 'src/utils/currency/formatAsCurrency';
import { trim } from 'src/utils/string/trim';
import { translate } from 'src/utils/translate/translate';

export function PaymentLinkPage() {
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [invalidPaymentLink, setInvalidPaymentLink] = useState(false);
    const [paymentLink, setPaymentLink] = useState();
    const downSm = useMediaQuery(theme.breakpoints.down('sm'));

    const form = useForm<FormType>();

    const onSubmit = async (form: FormType) => {
        if (!paymentLink) return;
        const response = await paymentLinkApi.payPaymentLink({
            paymentLinkId: paymentLink.paymentLinkId,
            email: trim(form.contact.email),
            phoneNumber: trim(form.contact.phoneNumber),
            cardNumber: form.card.cardNumber,
            expiryMonth: form.card.expiry?.substring(0, 2),
            expiryYear: form.card.expiry?.substring(2, 4),
            cvv: form.card.cvv,
            shippingAddress: paymentLink.askForShippingAddress
                ? {
                      addressLine1: trim(form.shippingAddress.addressLine1),
                      addressLine2: trim(form.shippingAddress.addressLine2),
                      zipCode: trim(form.shippingAddress.zipCode),
                      city: trim(form.shippingAddress.city),
                      state: trim(form.shippingAddress.state),
                  }
                : undefined,
        });
        if (!response.ok) {
            alertKnownErrorOrSomethingWentWrong(response);
            load();
            return;
        }
        if (!response.data.success) {
            alert(response.data.message);
            return;
        }
        load();
    };

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        setLoading(true);
        const paymentLinkId = getPaymentLinkIdFromUrl();
        if (!paymentLinkId) {
            setInvalidPaymentLink(true);
            return;
        }
        const response = await paymentLinkApi.getPaymentLink({
            paymentLinkId: paymentLinkId,
        });
        if (!response.ok) {
            setLoading(false);
            alertKnownErrorOrSomethingWentWrong(response);
            return;
        }
        setLoading(false);
        form.setValue('customerName', response.data.customerName);
        form.setValue('contact', {
            email: response.data.email,
            phoneNumber: response.data.phoneNumber,
        });
        if (response.data.askForShippingAddress) {
            setTimeout(() => {
                form.setValue('shippingAddress', response.data.shippingAddress);
            }, 0); // Use setTimeout so it gets set after from components are rendered because form inputs are toggled with askForShippingAddress
        }
        setPaymentLink(response.data);
    };

    if (invalidPaymentLink) {
        return <InvalidPaymentLink />;
    }

    if (paymentLink?.paymentLinkStatus === PaymentLinkStatuses.CANCELLED) {
        return <PaymentNotAvailable paymentLink={paymentLink} />;
    }

    if (paymentLink?.paymentLinkStatus === PaymentLinkStatuses.PAID) {
        return <PaymentLinkIsPaid paymentLink={paymentLink} />;
    }

    if (loading) {
        return <Loading />;
    }

    return (
        <Grid container spacing={0} classes={{ root: classes.container }}>
            <Grid item xs={12} md={6} container direction='column' justify='center' alignItems='center' classes={{ root: classes.leftContainer }}>
                <Typography variant='h2' align='center' color='textSecondary' className={classes.companyName}>
                    {paymentLink?.companyName}
                </Typography>
                <Typography variant='h4' align='center' color='textSecondary' className={classes.total}>
                    {formatAsCurrency(paymentLink?.total)}
                </Typography>
                {!!paymentLink?.msi ? (
                    <Typography variant='subtitle1' align='center' className={classes.helloText} color='primary'>
                        {translate('Hello @customer, you received a payment request of @amount every @msi months from @company', {
                            customer: paymentLink?.customerName,
                            amount: formatAsCurrency(calculateMsiAmount(paymentLink?.total, paymentLink?.msi)),
                            msi: paymentLink?.msi,
                            company: paymentLink?.companyName,
                        })}
                    </Typography>
                ) : (
                    <Typography variant='subtitle1' align='center' className={classes.helloText} color='primary'>
                        {translate('Hello @customer, you received a @amount payment request from @company', {
                            customer: paymentLink?.customerName,
                            amount: formatAsCurrency(paymentLink?.total),
                            company: paymentLink?.companyName,
                        })}
                    </Typography>
                )}
                {!!paymentLink?.reason && (
                    <Typography variant='subtitle1' align='center' className={classes.reasonText} color='primary'>
                        {translate('Reason')}: {paymentLink?.reason}
                    </Typography>
                )}
                <Grid container direction='row' justify='center' alignItems='center' className={classes.linksContainer}>
                    <Typography variant='caption' component='a' href={'http://openlinea.mx'} className={classes.link}>
                        Powered by <img src={openlinealogo} style={{ verticalAlign: 'bottom', marginBottom: 0.75 }} alt='Operaciones en Línea' height={18} />
                    </Typography>
                    <Typography variant='caption' component='a' href={'http://openlinea.mx/terms'} className={classes.link}>
                        {translate('Terms and Conditions')}
                    </Typography>
                    <Typography variant='caption' component='a' href={'http://openlinea.mx/privacy'} className={classes.link}>
                        {translate('Privacy')}
                    </Typography>
                </Grid>
            </Grid>
            <Grid item xs={12} md={6} container direction='row' justify='center' alignItems='center' classes={{ root: classes.rightContainer }}>
                <Form form={form} onSubmit={onSubmit} className={classes.form}>
                    <Grid container spacing={downSm ? 1 : 2}>
                        <Grid item xs={12}>
                            <FormContactDetailsField name='contact' />
                        </Grid>

                        <Grid item xs={12}>
                            {/*<Divider />*/}
                        </Grid>

                        <Grid item xs={12}>
                            <FormCardDetailsField name='card' required />
                        </Grid>

                        <Grid item xs={12}>
                            {/*<Divider />*/}
                        </Grid>

                        {paymentLink?.askForShippingAddress && (
                            <Grid item xs={12}>
                                <FormShippingDetailsField name='shippingAddress' required />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            {/*<Divider />*/}
                        </Grid>

                        <Grid item xs={12}>
                            <Button variant='contained' color='primary' type='submit' fullWidth disabled={form.formState.isSubmitting} style={{ height: 56 }}>
                                {form.formState.isSubmitting && <CircularProgress color='primary' className={classes.buttonProgress} />}
                                {translate('Pay @amount', { amount: formatAsCurrency(paymentLink?.total) })}
                            </Button>
                        </Grid>

                        <Grid item xs={12}>
                            <Box align='center' color='text.disabled'>
                                <Typography variant='caption'>{translate('The charge on your bank statement may appear as OPENLIN*OPERAENLINEA')}</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Form>
            </Grid>
        </Grid>
    );
}

function getPaymentLinkIdFromUrl() {
    return window.location.pathname.match(/[0-9A-Za-z]{10}/)?.[0];
}

function calculateMsiAmount(total: string, msi: number): string {
    return BigNumber(total).dividedBy(msi).toFixed(2);
}

const useStyles = makeStyles(theme => ({
    container: {
        height: 'auto',
        [theme.breakpoints.up('md')]: {
            height: '100%',
        },
    },
    leftContainer: {
        padding: theme.spacing(2),
        backgroundColor: '#fafafa',
        borderWidth: 0,
        borderBottomWidth: 1,
        borderColor: '#eee',
        borderStyle: 'solid',
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(4),
            borderRightWidth: 1,
        },
    },
    rightContainer: {
        padding: theme.spacing(2),
        backgroundColor: 'white',
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(4),
        },
    },
    companyName: {
        paddingTop: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
            paddingTop: theme.spacing(0),
        },
    },
    total: {
        paddingTop: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
            paddingTop: theme.spacing(12),
        },
    },
    helloText: {
        paddingTop: theme.spacing(6),
        [theme.breakpoints.up('md')]: {
            paddingTop: theme.spacing(9),
        },
    },
    reasonText: {
        paddingTop: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
            paddingTop: theme.spacing(9),
        },
    },
    linksContainer: {
        paddingTop: theme.spacing(6),
        [theme.breakpoints.up('md')]: {
            paddingTop: theme.spacing(22),
        },
        align: 'center',
    },
    link: {
        color: theme.palette.text.disabled,
        textDecoration: 'none',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    form: {
        maxWidth: 500,
    },
    buttonProgress: {
        position: 'absolute',
    },
}));

type FormType = {|
    contact: {|
        email: EmailAddress,
        phoneNumber: PhoneNumber,
    |},
    card: {|
        cardNumber: string,
        expiry: string,
        cvv: string,
    |},
    shippingAddress: {|
        addressLine1: string,
        addressLine2: string,
        zipCode: string,
        city: string,
        state: string,
    |},
|};
